import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import { directMutations } from '../utils/auth-helpers';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { type Doc } from '@/convex/_generated/dataModel';
import { useAuth } from '@/hooks/useAuth';

export function DebugPage() {
	const {
		user: web3AuthUser,
		isLoggedIn,
		logout,
		isWeb3AuthLoading,
	} = useWeb3Auth();
	const { user: convexUser, isAdmin, isLoading: authLoading } = useAuth();
	const [directQueryResult, setDirectQueryResult] =
		useState<Doc<'users'> | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Regular Convex query
	const normalQueryResult = useQuery(
		api.users.getByEmail,
		web3AuthUser?.email ? { email: web3AuthUser.email } : 'skip'
	);

	// Function to test direct query
	const testDirectQuery = async () => {
		if (!web3AuthUser?.email) {
			setError('No email available from Web3Auth');
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const result = await directMutations.users.getByEmail({
				email: web3AuthUser.email,
			});
			setDirectQueryResult(result);
		} catch (err) {
			console.error('Error in direct query test:', err);
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setIsLoading(false);
		}
	};

	// Log complete state for debugging
	useEffect(() => {
		console.log('Debug Page State:', {
			web3AuthUser: web3AuthUser,
			isLoggedIn,
			isWeb3AuthLoading,
			convexUser,
			isAdmin,
			authLoading,
			normalQueryResult:
				normalQueryResult === undefined ? 'loading' : normalQueryResult,
		});
	}, [
		web3AuthUser,
		isLoggedIn,
		isWeb3AuthLoading,
		convexUser,
		isAdmin,
		authLoading,
		normalQueryResult,
	]);

	return (
		<div className='container mx-auto p-8'>
			<h1 className='text-2xl font-bold mb-6'>Debug Convex Connection</h1>

			<div className='flex gap-4 mb-8'>
				<Button
					className='bg-green-600 hover:bg-green-700'
					onClick={() =>
						alert(
							'Login should be initiated via ConnectButton or navigation to login page.'
						)
					}
				>
					Log In with Web3Auth
				</Button>
				<Button onClick={logout} variant='destructive'>
					Log Out
				</Button>
			</div>

			<div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-8'>
				<div className='p-4 bg-gray-100 rounded-lg'>
					<h2 className='text-xl font-semibold mb-2'>
						Web3Auth State
					</h2>
					<div className='bg-white p-3 rounded mb-2'>
						<p>
							<strong>isLoggedIn:</strong>{' '}
							{isLoggedIn ? 'true' : 'false'}
						</p>
						<p>
							<strong>isLoading:</strong>{' '}
							{isWeb3AuthLoading ? 'true' : 'false'}
						</p>
					</div>

					<h2 className='text-xl font-semibold mb-2'>
						Web3Auth User Info
					</h2>
					{web3AuthUser ? (
						<pre className='bg-white p-3 rounded max-h-64 overflow-auto'>
							{JSON.stringify(web3AuthUser, null, 2)}
						</pre>
					) : (
						<p className='text-orange-500 bg-white p-3 rounded'>
							No Web3Auth user is logged in
						</p>
					)}
				</div>

				<div className='p-4 bg-gray-100 rounded-lg'>
					<h2 className='text-xl font-semibold mb-2'>
						Convex Auth State
					</h2>
					<div className='bg-white p-3 rounded mb-2'>
						<p>
							<strong>isAdmin:</strong>{' '}
							{isAdmin ? 'true' : 'false'}
						</p>
						<p>
							<strong>isLoading:</strong>{' '}
							{authLoading ? 'true' : 'false'}
						</p>
					</div>

					<h2 className='text-xl font-semibold mb-2'>Convex User</h2>
					{convexUser ? (
						<pre className='bg-white p-3 rounded max-h-64 overflow-auto'>
							{JSON.stringify(convexUser, null, 2)}
						</pre>
					) : (
						<p className='text-orange-500 bg-white p-3 rounded'>
							No Convex user found
						</p>
					)}
				</div>
			</div>

			<div className='mb-8 p-4 bg-gray-100 rounded-lg'>
				<h2 className='text-xl font-semibold mb-2'>
					Normal Convex Query Result
				</h2>
				{normalQueryResult === undefined ? (
					<p className='text-blue-500 bg-white p-3 rounded'>
						Loading...
					</p>
				) : normalQueryResult === null ? (
					<p className='text-orange-500 bg-white p-3 rounded'>
						No result from normal query
					</p>
				) : (
					<pre className='bg-white p-3 rounded max-h-64 overflow-auto'>
						{JSON.stringify(normalQueryResult, null, 2)}
					</pre>
				)}
			</div>

			<div className='mb-8'>
				<Button
					onClick={testDirectQuery}
					disabled={isLoading || !web3AuthUser?.email}
					className='mb-4'
				>
					{isLoading ? 'Testing...' : 'Test Direct Query'}
				</Button>

				{error && (
					<div className='mb-4 p-3 bg-red-100 text-red-800 rounded'>
						Error: {error}
					</div>
				)}

				<h2 className='text-xl font-semibold mb-2'>
					Direct Query Result
				</h2>
				{directQueryResult ? (
					<pre className='bg-white p-3 rounded border max-h-64 overflow-auto'>
						{JSON.stringify(directQueryResult, null, 2)}
					</pre>
				) : (
					<p className='text-gray-500 bg-white p-3 rounded'>
						No direct query has been performed yet
					</p>
				)}
			</div>
		</div>
	);
}
